import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Drawdown, Facility, Repayment } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { ConfirmDialog } from '../common/ConfirmDialog';
import FacilityFormDialog from './FacilityFormDialog';
import DrawdownDialog from './DrawdownDialog';
import RepaymentDialog from './RepaymentDialog';

const FacilityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.FINANCING_MANAGE);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [drawdowns, setDrawdowns] = useState<Drawdown[]>([]);
  const [repayments, setRepayments] = useState<Repayment[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [repaying, setRepaying] = useState<Drawdown | null>(null);
  const [voiding, setVoiding] = useState<{ kind: 'drawdown' | 'repayment'; id: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState({ effectiveDate: toLocalIsoDate(), ratePct: '', note: '' });

  const load = useCallback(async () => {
    try {
      const [f, d, r] = await Promise.all([BorrowingService.getFacility(id!), BorrowingService.listDrawdowns(id!), BorrowingService.listRepayments(id!)]);
      setFacility(f); setDrawdowns(d); setRepayments(r);
    } catch (err) {
      toast.showApiError(err, 'Could not load the facility');
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    try { await fn(); toast.showSuccess(ok); await load(); } catch (err) { toast.showApiError(err, fail); }
  };

  const confirmVoid = async () => {
    if (!voiding || busy) return; // busy guard: a double click must not send two voids
    setBusy(true);
    try {
      if (voiding.kind === 'drawdown') await BorrowingService.voidDrawdown(voiding.id);
      else await BorrowingService.voidRepayment(voiding.id);
      toast.showSuccess(voiding.kind === 'drawdown' ? 'Drawdown voided' : 'Repayment voided');
      setVoiding(null);
      await load();
    } catch (err) {
      toast.showApiError(err, voiding.kind === 'drawdown' ? 'Could not void the drawdown' : 'Could not void the repayment');
      setVoiding(null);
    } finally {
      setBusy(false);
    }
  };

  if (!facility) return <Box sx={{ p: 2 }}><Typography>Loading...</Typography></Box>;
  const cur = facility.currencyCode;
  // Only the latest posted repayment of a drawdown can be voided. The API lists newest first, so on a
  // date tie the first one seen wins; never rely on array order alone.
  const latestRepayment = new Map<string, Repayment>();
  repayments.filter(r => r.status === 'POSTED').forEach(r => {
    const seen = latestRepayment.get(r.drawdownId);
    if (!seen || r.paymentDate > seen.paymentDate) latestRepayment.set(r.drawdownId, r);
  });
  const latestRepaymentByDrawdown = new Map<string, string>();
  latestRepayment.forEach((r, drawdownId) => latestRepaymentByDrawdown.set(drawdownId, r.id));

  return (
    <Box data-help-page="financing.detail.page" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 260px' }}>{facility.facilityNo} - {facility.name}</Typography>
        <Chip label={humanizeEnum(facility.status)} color={facility.status === 'ACTIVE' ? 'success' : 'default'} />
        <Button component={RouterLink} to="/financing">Back</Button>
        {canManage && <Button data-help="financing.detail.action.edit" onClick={() => setEditing(true)}>Edit</Button>}
        {canManage && facility.status === 'ACTIVE' && (
          <Button data-help="financing.detail.action.close" onClick={() => act(() => BorrowingService.closeFacility(facility.id), 'Facility closed', 'Could not close the facility')}>Close</Button>
        )}
        {canManage && facility.status === 'CLOSED' && (
          <Button data-help="financing.detail.action.reopen" onClick={() => act(() => BorrowingService.reopenFacility(facility.id), 'Facility reopened', 'Could not reopen the facility')}>Reopen</Button>
        )}
      </Box>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Typography variant="body2">Type: {humanizeEnum(facility.facilityType)}</Typography>
        <Typography variant="body2">Lender: {facility.lenderName}</Typography>
        <Typography variant="body2">Limit: {formatMoney(facility.sanctionedLimit, cur)}</Typography>
        <Typography variant="body2">Rate: {facility.currentRatePct ?? '-'}% {facility.rateType === 'FLOATING' ? `(base + ${facility.spreadPct ?? 0})` : ''}</Typography>
        <Typography variant="body2">Valid: {facility.validFrom ? formatDate(facility.validFrom) : '-'} to {facility.validTo ? formatDate(facility.validTo) : '-'}</Typography>
        <Typography variant="body2">Day count: {humanizeEnum(facility.dayCount)}</Typography>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Rate history</Typography>
      <TableContainer data-help-section="financing.detail.rates" component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="financing.detail.rates.col.effective">Effective</TableCell><TableCell data-help="financing.detail.rates.col.rate" align="right">Rate %</TableCell><TableCell data-help="financing.detail.rates.col.note">Note</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {facility.rates.map(r => (
              <TableRow key={r.id}>
                <TableCell>{formatDate(r.effectiveDate)}</TableCell><TableCell align="right">{r.ratePct}</TableCell><TableCell>{r.note}</TableCell>
                <TableCell align="right">
                  {canManage && <Button data-help="financing.detail.rates.action.remove" size="small" color="error" onClick={() => act(() => BorrowingService.deleteRate(facility.id, r.id), 'Rate removed', 'Could not remove the rate')}>Remove</Button>}
                </TableCell>
              </TableRow>
            ))}
            {canManage && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box data-help-section="financing.detail.addRate" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <TextField data-help="financing.detail.rates.effective" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="date" label="Effective" InputLabelProps={{ shrink: true }}
                      value={rate.effectiveDate} onChange={e => setRate(s => ({ ...s, effectiveDate: e.target.value }))} />
                    <TextField data-help="financing.detail.rates.ratePct" sx={{ flex: '1 1 120px', maxWidth: 160 }} size="small" type="number" label="Rate %" value={rate.ratePct}
                      onChange={e => setRate(s => ({ ...s, ratePct: e.target.value }))} />
                    <TextField data-help="financing.detail.rates.noteField" sx={{ flex: '1 1 200px', maxWidth: 300 }} size="small" label="Note" value={rate.note} onChange={e => setRate(s => ({ ...s, note: e.target.value }))} />
                    <Button data-help="financing.detail.rates.add" variant="outlined" disabled={rate.ratePct === ''} onClick={() => act(
                      () => BorrowingService.addRate(facility.id, { effectiveDate: rate.effectiveDate, ratePct: Number(rate.ratePct), note: rate.note || undefined }),
                      'Rate added', 'Could not add the rate').then(() => setRate(s => ({ ...s, ratePct: '', note: '' })))}>Add rate</Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>Drawdowns</Typography>
        {canManage && facility.status === 'ACTIVE' && <Button data-help="financing.detail.drawdowns.new" variant="contained" onClick={() => setDrawing(true)}>New drawdown</Button>}
      </Box>
      {drawdowns.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No drawdowns yet.</Alert>}
      {drawdowns.length > 0 && (
        <TableContainer data-help-section="financing.detail.drawdowns" component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell /><TableCell data-help="financing.detail.drawdowns.col.no">No</TableCell><TableCell data-help="financing.detail.drawdowns.col.date">Date</TableCell><TableCell data-help="financing.detail.drawdowns.col.repayment">Repayment</TableCell>
                <TableCell data-help="financing.detail.drawdowns.col.amount" align="right">Amount</TableCell><TableCell data-help="financing.detail.drawdowns.col.outstanding" align="right">Outstanding</TableCell><TableCell data-help="financing.detail.drawdowns.col.status">Status</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {drawdowns.map(d => (
                <React.Fragment key={d.id}>
                  <TableRow hover>
                    <TableCell><IconButton data-help="financing.detail.drawdowns.action.schedule" size="small" aria-label="Toggle schedule" onClick={() => setOpen(open === d.id ? null : d.id)}>
                      {open === d.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                    <TableCell>{d.drawdownNo}{d.opening ? ' (opening)' : ''}</TableCell>
                    <TableCell>{formatDate(d.drawdownDate)}</TableCell>
                    <TableCell>{humanizeEnum(d.repaymentType)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(d.amount)}
                      {d.foreignAmount != null && cur && cur !== 'BDT' && (
                        <Typography variant="caption" color="text.secondary" component="div" data-testid={`drawdown-fc-${d.id}`}>
                          {formatMoney(d.foreignAmount, cur)}{d.exchangeRate != null ? ` @ ${d.exchangeRate}` : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatMoney(d.outstanding)}</TableCell>
                    <TableCell>{humanizeEnum(d.status)}</TableCell>
                    <TableCell align="right">
                      {d.journalEntryId && <Button data-help="financing.detail.drawdowns.action.journal" size="small" component={RouterLink} to={`/accounting/journal-entries/${d.journalEntryId}`}>Journal</Button>}
                      {canManage && d.status === 'ACTIVE' && <Button data-help="financing.detail.drawdowns.action.repay" size="small" onClick={() => setRepaying(d)}>Repay</Button>}
                      {canManage && d.status === 'ACTIVE' && (
                        <Button data-help="financing.detail.drawdowns.action.void" size="small" color="error" onClick={() => setVoiding({ kind: 'drawdown', id: d.id, label: `drawdown ${d.drawdownNo}` })}>Void</Button>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, border: open === d.id ? undefined : 0 }}>
                      <Collapse in={open === d.id} unmountOnExit>
                        <Table data-help-section="financing.detail.schedule" size="small" sx={{ my: 1 }}>
                          <TableHead><TableRow><TableCell data-help="financing.detail.schedule.col.seq">#</TableCell><TableCell data-help="financing.detail.schedule.col.due">Due</TableCell><TableCell data-help="financing.detail.schedule.col.principal" align="right">Principal</TableCell>
                            <TableCell data-help="financing.detail.schedule.col.paid" align="right">Paid</TableCell><TableCell data-help="financing.detail.schedule.col.status">Status</TableCell></TableRow></TableHead>
                          <TableBody>
                            {d.schedule.map(s => (
                              <TableRow key={s.id}><TableCell>{s.seq}</TableCell><TableCell>{formatDate(s.dueDate)}</TableCell>
                                <TableCell align="right">{formatMoney(s.principalDue)}</TableCell><TableCell align="right">{formatMoney(s.principalPaid)}</TableCell>
                                <TableCell>{humanizeEnum(s.status)}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>Repayments</Typography>
      {repayments.length === 0 ? <Alert severity="info">No repayments yet.</Alert> : (
        <TableContainer data-help-section="financing.detail.repayments" component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell data-help="financing.detail.repayments.col.date">Date</TableCell><TableCell data-help="financing.detail.repayments.col.total" align="right">Total</TableCell><TableCell data-help="financing.detail.repayments.col.interest" align="right">Interest</TableCell>
              <TableCell data-help="financing.detail.repayments.col.principal" align="right">Principal</TableCell><TableCell data-help="financing.detail.repayments.col.prepaid" align="right">Prepaid</TableCell><TableCell data-help="financing.detail.repayments.col.charges" align="right">Charges</TableCell><TableCell data-help="financing.detail.repayments.col.status">Status</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {repayments.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{formatDate(r.paymentDate)}</TableCell><TableCell align="right">{formatMoney(r.totalAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(r.interestAmount)}</TableCell><TableCell align="right">{formatMoney(r.principalAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(r.prepaymentAmount)}</TableCell><TableCell align="right">{formatMoney(r.chargesAmount)}</TableCell>
                  <TableCell>{humanizeEnum(r.status)}</TableCell>
                  <TableCell align="right">
                    {r.journalEntryId && <Button data-help="financing.detail.repayments.action.journal" size="small" component={RouterLink} to={`/accounting/journal-entries/${r.journalEntryId}`}>Journal</Button>}
                    {canManage && r.status === 'POSTED' && latestRepaymentByDrawdown.get(r.drawdownId) === r.id && (
                      <Button data-help="financing.detail.repayments.action.void" size="small" color="error" onClick={() => setVoiding({ kind: 'repayment', id: r.id, label: `the repayment of ${formatDate(r.paymentDate)}` })}>Void</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog open={!!voiding} title="Void this entry?" confirmLabel="Void" confirmColor="error" submitting={busy}
        message={voiding ? `Void ${voiding.label}? A reversing journal is posted and the schedule is restored. This cannot be undone.` : ''}
        onCancel={() => setVoiding(null)} onConfirm={confirmVoid} />
      <FacilityFormDialog open={editing} institutionId={facility.institutionId} facility={facility} onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); load(); }} />
      <DrawdownDialog open={drawing} facility={facility} onClose={() => setDrawing(false)} onSaved={() => { setDrawing(false); load(); }} />
      <RepaymentDialog open={!!repaying} institutionId={facility.institutionId} drawdown={repaying} defaultBankId={facility.bankAccountId}
        currency={cur} onClose={() => setRepaying(null)} onSaved={() => { setRepaying(null); load(); }} />
    </Box>
  );
};

export default FacilityDetail;
